import React, { Ref } from 'react'
import { FileRejection } from 'react-dropzone'

import { type Dimensions, useEffect, useUnsafeEffect } from '@factorialco/deprecated-design-system'
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import { $createListNode, $isListNode } from '@lexical/list'
import { ClearEditorPlugin } from '@lexical/react/LexicalClearEditorPlugin'
import type { InitialEditorStateType } from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import {
  $createParagraphNode,
  $getRoot,
  $isLineBreakNode,
  $isTextNode,
  $setSelection,
  CLEAR_EDITOR_COMMAND,
  COMMAND_PRIORITY_EDITOR,
  FOCUS_COMMAND,
} from 'lexical'

import { styles } from './index.styles'
import './index.styles.css'
import { ClearEmptyEditorPlugin } from './plugins/ClearEmptyEditorPlugin'
import ClickableLinkPlugin from './plugins/ClickableLinkPlugin'
import type { MentionedAccess } from './plugins/DebouncedMentionsPlugin'
import DebouncedMentionsPlugin from './plugins/DebouncedMentionsPlugin'
import FileList, { CLEAR_FILES_COMMAND } from './plugins/FileAttachment'
import FloatingLinkEditorPlugin from './plugins/FloatingLinkEditorPlugin'
import { LinkPlugin } from './plugins/LinkPlugin'
import MentionsPlugin, { MentionedUser } from './plugins/MentionsPlugin'
import ToolbarPlugin from './plugins/ToolbarPlugin'
import { useEditor } from './useEditor'

export type onFiles = (files: File[]) => void

type editorDimensions = Dimensions

export interface MentionChangeResult {
  value: string
  ids: string[]
}

export interface EditorProps {
  onChange: (html: string | null | MentionChangeResult) => void
  onFiles?: onFiles
  initialEditorState?: {
    data?: InitialEditorStateType
    files?: File[]
  }
  /**
   * Limits the maximum allowed file size (in MB) for file upload size.
   */
  maxFileSize?: number
  height?: editorDimensions
  placeholder: string
  readonly?: boolean
  isRichText?: boolean
  enableBetaFeatures?: boolean
  missingLabel: string
  selectValue: string
  onDropRejected: (rejections: FileRejection[]) => void
  saveButtonLabel: string
  removeButtonLabel: string
  errorLabelFn: (validUrl: boolean, linkUrl: string) => string | undefined
  hasMentions?: boolean
  hasDebouncedMentions?: boolean
  onMentionQueryStringChanged?: (queryString: string) => Promise<MentionedAccess[]> | undefined
  users?: MentionedUser[]
}

export interface EditorHandle {
  clear: () => void
  clearFiles: () => void
  focus: () => void
}

const Editor = React.forwardRef(function Editor(
  {
    onChange,
    onFiles,
    initialEditorState,
    maxFileSize,
    height = 's96',
    placeholder,
    readonly,
    isRichText,
    enableBetaFeatures,
    missingLabel,
    selectValue,
    onDropRejected,
    saveButtonLabel,
    removeButtonLabel,
    errorLabelFn,
    hasMentions = false,
    hasDebouncedMentions = false,
    onMentionQueryStringChanged,
    users,
  }: EditorProps,
  ref: Ref<EditorHandle>
) {
  const EMPTY_STATE = isRichText ? '<p class="TextEditorTheme__paragraph"><br></p>' : ''
  const isEditorInitialized = React.useRef(false)
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const editor = useEditor()

  React.useImperativeHandle(ref, () => ({
    clear: () => editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined),
    clearFiles: () => editor.dispatchCommand(CLEAR_FILES_COMMAND, {}),
    focus: () => editor.dispatchCommand(FOCUS_COMMAND, new FocusEvent('focus')),
  }))

  const handleOnChange = () => {
    if (!editor.isEditable()) {
      return
    }

    editor.update(() => {
      const html = $generateHtmlFromNodes(editor, null)
      onChange(html === EMPTY_STATE ? null : html)
    })
  }

  const loadHtml = () => {
    const parser = new DOMParser()
    const dom = parser.parseFromString(initialEditorState?.data as string, 'text/html')
    const nodes = $generateNodesFromDOM(editor, dom)
    const root = $getRoot()

    if (!isRichText) {
      root.clear()
    }

    nodes.forEach((node) => {
      let appendableNode = node

      if ($isTextNode(node) || $isLineBreakNode(node)) {
        const paragraphNode = $createParagraphNode()
        paragraphNode.append(appendableNode)
        appendableNode = paragraphNode
      }

      if ($isListNode(node)) {
        const nonEmptyNodeChildren = node.getChildren().filter((child) => child.getTextContent())

        const listNode = $createListNode(node.__listType, 1)
        listNode.append(...nonEmptyNodeChildren)
        appendableNode = listNode
      }

      root.append(appendableNode)
    })
  }

  const handleEditorInitialization = () => {
    editor.update(() => {
      loadHtml()
      isEditorInitialized.current = true
    })
  }

  const updateHtml = () => {
    editor.update(() => {
      if (
        isEditorInitialized.current === false ||
        initialEditorState?.data === $generateHtmlFromNodes(editor, null) ||
        (!isRichText && initialEditorState?.data !== '')
      ) {
        return
      }

      $getRoot().clear()
      loadHtml()
      $setSelection(null)
    })
  }

  useEffect(() => {
    handleEditorInitialization()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    editor.setEditable(!readonly)
  }, [readonly])

  useUnsafeEffect(() => {
    if (initialEditorState?.data) updateHtml()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEditorState?.data])

  useUnsafeEffect(() => {
    return editor.registerCommand(
      FOCUS_COMMAND,
      () => {
        editor.focus()
        return true
      },
      COMMAND_PRIORITY_EDITOR
    )
  }, [editor])

  useUnsafeEffect(() => {
    // NOTE: The `CLEAR_EDITOR_COMMAND` constant is defined in the `lexical` package.
    //       However when dispatching this command it didn't work. So, I had to define it here.
    return editor.registerCommand(
      CLEAR_EDITOR_COMMAND,
      () => {
        $getRoot().clear()
        return true
      },
      COMMAND_PRIORITY_EDITOR
    )
  }, [editor])

  const editorScrollerStyles = ['TextEditor_editorScroller', styles.height[height]].join(' ')

  const handleMentionsOnChange = () => {
    if (!editor.isEditable()) {
      return
    }

    editor.update(() => {
      const ids: string[] = []
      const editorContent = $generateHtmlFromNodes(editor, null)
      const mentionsList = editor._rootElement?.querySelectorAll<HTMLElement>('[data-lexical-id]')

      mentionsList?.forEach((item) => {
        const userId = String(item.getAttribute('data-lexical-id'))
        const userName = item.innerText

        if (!!userId && !ids.includes(userId)) {
          const mentionedCount =
            editor._rootElement?.querySelectorAll('[data-lexical-id="' + userId + '"]').length || 0
          const regex = new RegExp(userName, 'gm')
          const found = editorContent.match(regex) || []

          if (found.length - mentionedCount === 0) {
            ids.push(userId)
          }
        }
      })

      const data = { value: editorContent, ids }

      onChange(data)
    })
  }

  return (
    <>
      {isRichText ? (
        <>
          <ToolbarPlugin
            onFiles={onFiles}
            enableBetaFeatures={enableBetaFeatures}
            maxFileSize={maxFileSize}
            onDropRejected={onDropRejected}
          />
          <div className={'TextEditor_editorContainer'} ref={scrollRef}>
            <OnChangePlugin
              onChange={() => {
                if (hasMentions || hasDebouncedMentions) {
                  return handleMentionsOnChange()
                }
                return handleOnChange()
              }}
              ignoreSelectionChange
            />
            <ClearEditorPlugin />
            <ClearEmptyEditorPlugin />
            <RichTextPlugin
              contentEditable={
                <div className={editorScrollerStyles}>
                  <div className={'TextEditor_editorContent'}>
                    <ContentEditable className={'TextEditor_contentEditableRoot'} />
                  </div>
                </div>
              }
              placeholder={<div className={'TextEditor_placeholderRoot'}>{placeholder}</div>}
              ErrorBoundary={LexicalErrorBoundary}
            />
            <ListPlugin />
            <LinkPlugin />
            <ClickableLinkPlugin />
            {scrollRef.current && (
              <FloatingLinkEditorPlugin
                anchorElem={scrollRef.current}
                missingLabel={missingLabel}
                selectValue={selectValue}
                saveButtonLabel={saveButtonLabel}
                removeButtonLabel={removeButtonLabel}
                errorLabelFn={errorLabelFn}
              />
            )}
            <FileList onFiles={onFiles} initialFiles={initialEditorState?.files} />
            <>
              {hasMentions && <MentionsPlugin users={users} />}
              {hasDebouncedMentions && onMentionQueryStringChanged && (
                <DebouncedMentionsPlugin
                  editor={editor}
                  onMentionQueryStringChanged={onMentionQueryStringChanged}
                />
              )}
            </>
          </div>
        </>
      ) : (
        <div
          className={'TextEditor_editorContainer' + ' ' + 'TextEditor_plainEditorContainer'}
          ref={scrollRef}
        >
          <OnChangePlugin
            onChange={() => {
              if (hasMentions || hasDebouncedMentions) {
                return handleMentionsOnChange()
              }
              return handleOnChange()
            }}
            ignoreSelectionChange
          />
          <PlainTextPlugin
            contentEditable={
              <div className={editorScrollerStyles}>
                <div className={'TextEditor_editorContent'}>
                  <ContentEditable className={'TextEditor_contentEditableRoot'} />
                </div>
              </div>
            }
            placeholder={<div className={'TextEditor_placeholderRoot'}>{placeholder}</div>}
            ErrorBoundary={LexicalErrorBoundary}
          />
          {hasMentions && <MentionsPlugin users={users} />}
          {hasDebouncedMentions && onMentionQueryStringChanged && (
            <DebouncedMentionsPlugin
              editor={editor}
              onMentionQueryStringChanged={onMentionQueryStringChanged}
            />
          )}
        </div>
      )}
    </>
  )
})

export default Editor
